import classNames from 'classnames';
import * as React from 'react';
import { v4 as uuid } from 'uuid';

import Spinner from '../../atoms/Spinner';
import Text from '../../atoms/Text';
import CustomTooltipInfo from '../../atoms/CustomTooltipInfo';
import SearchBar from '../../molecules/SearchBar';
import PillsFilter from '../../molecules/PillsFilter';

import styles from './HierarchicalSelect.module.scss';
import { HierarchicalSelectOption, ValueMap } from './types';
import {
  filterHierarchyByLabel,
  filterHierarchyBySelection,
  flattenHierarchicalData,
  updateHierarchicalSelection,
} from './utils';
import SelectSection from './SelectSection';
import useExitingOptions from './useExitingOptions';

export interface Props
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'size' | 'onChange'
  > {
  loading?: boolean;
  onOpen?: () => void;
  height?: number;
  disabled?: boolean;
  search?: string;
  onSearch?: (search: string) => void;
  searchPlaceholder?: string;
  multiple?: boolean;
  /** Shows an internal "All / Selected" filter above the list (requires multiple) */
  selectionFilter?: boolean;
  label?: string;
  tooltip?: string;
  description?: string;
  helperText?: string;
  errorText?: string;
  defaultExpanded?: boolean;
  maxDepth?: 0 | 1 | 2;
  value?: ValueMap;
  onChange: (value: ValueMap) => void;
  options: HierarchicalSelectOption[];
  footer?: React.ReactNode;
}

/**
 * Retrieves the IDs to expand and display
 */
const getExpandedId = (
  options: HierarchicalSelectOption[],
  depth: number,
  maxDepth: number,
): string[][] => {
  const expandedArr: string[][] = [];

  expandedArr.push(options.map((opt) => opt.id));
  if (depth <= maxDepth) {
    const nextDepthArr: HierarchicalSelectOption[] = [];
    options.forEach((opt) => {
      if (opt.children) nextDepthArr.push(...opt.children);
    });
    if (nextDepthArr.length > 0) {
      expandedArr.push(...getExpandedId(nextDepthArr, depth + 1, maxDepth));
    }
  }

  return expandedArr;
};

/**
 * <HierarchicalSelect> component
 */
export const HierarchicalSelect = ({
  id,
  name,
  className,
  style,
  loading,
  value,
  label,
  tooltip,
  height,
  search: controlledSearch,
  searchPlaceholder = 'Pesquisar',
  options,
  description,
  helperText,
  errorText,
  onOpen,
  onChange,
  onSearch,
  multiple = false,
  selectionFilter = false,
  disabled = false,
  defaultExpanded = false,
  maxDepth = 1,
  footer,
  ...inputProps
}: React.PropsWithChildren<Props>) => {
  const [selected, setSelected] = React.useState<ValueMap>(value || {});
  const [search, setSearch] = React.useState('');
  const [selectionView, setSelectionView] = React.useState<'all' | 'selected'>(
    'all',
  );
  const viewportRef = React.useRef<HTMLDivElement | null>(null);

  const [expanded, setExpanded] = React.useState<string[][]>(
    defaultExpanded ? getExpandedId(options, 0, maxDepth) : [],
  );

  // Sync internal state with external state
  React.useEffect(() => {
    setSelected(value || {});
  }, [value]);

  // Flat options once to improve performance
  const flatOptions = React.useMemo(
    () => flattenHierarchicalData(options),
    [options],
  );

  const filteredOptions = React.useMemo(() => {
    const byLabel = filterHierarchyByLabel(options, search);
    if (!selectionFilter || selectionView === 'all') return byLabel;
    return filterHierarchyBySelection(byLabel, selected, flatOptions);
  }, [options, search, selectionFilter, selectionView, selected, flatOptions]);

  // Keep deselected items mounted briefly so they can fade out in the
  // "selected" view. Switching view or searching snaps instantly (resetKey).
  const displayedOptions = useExitingOptions(filteredOptions, {
    enabled: selectionFilter && selectionView === 'selected',
    resetKey: `${selectionView}|${search}`,
  });

  const inputId = id ?? uuid();

  /**
   * Handle change to selection when popover is open
   */
  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, _depth: number) => {
      const newSelection = multiple ? { ...selected } : {};

      flatOptions
        .filter((opt) => opt.path.includes(event.target.value))
        .forEach((opt) => {
          updateHierarchicalSelection(
            newSelection,
            opt.path,
            event.target.checked,
          );
        });

      setSelected(newSelection);
      onChange?.(newSelection);
    },
    [flatOptions, multiple, onChange, selected],
  );

  /**
   * Handle item expansion
   */
  const handleExpand = React.useCallback((rowId: string, depth: number) => {
    setExpanded((prev) => {
      const result = [...prev];
      result[depth] = [...(prev[depth] || []), rowId];
      return result;
    });
  }, []);

  /**
   * Handle item collapse
   */
  const handleCollapse = React.useCallback((rowId: string, depth: number) => {
    setExpanded((prev) => {
      return prev.map((p, idx) =>
        idx === depth ? p?.filter((i) => i !== rowId) : p,
      );
    });
  }, []);

  const cls = React.useMemo(
    () =>
      classNames(styles['label-wrapper'], className, {
        disabled,
        error: !!errorText,
      }),
    [className, disabled, errorText],
  );

  return (
    <div className={cls} style={style}>
      {label && (
        <div className="d-flex gap-1 text-items-center mb-2">
          <Text as="label" htmlFor={inputId} weight="bold">
            {label}
          </Text>
          {tooltip && <CustomTooltipInfo info={tooltip} />}
        </div>
      )}

      {description && (
        <Text as="span" className="d-block mb-2" size="sm2" color="blackLight">
          {description}
        </Text>
      )}

      <div className={styles['input-wrapper']}>
        <div className="p-3">
          <SearchBar
            id={inputId}
            className={styles.searchbar}
            placeholder={searchPlaceholder}
            value={controlledSearch || search}
            size="sm"
            onChange={(e) => {
              if (!controlledSearch) {
                setSearch(e.target.value);
              }
              onSearch?.(e.target.value);
            }}
            disabled={disabled}
            {...inputProps}
          />

          {selectionFilter && multiple && (
            <PillsFilter
              name="selectionView"
              align="left"
              options={[
                { id: 'all', label: 'Todos' },
                { id: 'selected', label: 'Selecionados' },
              ]}
              value={selectionView}
              onChange={(_, v) => setSelectionView(v as 'all' | 'selected')}
            />
          )}

          <div
            className={styles['inner-box']}
            ref={viewportRef}
            style={{ height }}
            role="listbox"
          >
            {loading && <Spinner size="sm" />}

            {!loading && displayedOptions.length === 0 && (
              <Text size="sm2" color="blackLight">
                Nenhum dado encontrado
              </Text>
            )}

            {!loading &&
              displayedOptions.map(({ option, leaving }) => (
                <SelectSection
                  key={option.id}
                  option={option}
                  leaving={leaving}
                  expanded={expanded}
                  selection={selected}
                  maxDepth={maxDepth}
                  flatOptions={flatOptions}
                  onChange={handleChange}
                  onExpand={handleExpand}
                  onCollapse={handleCollapse}
                  multiple={multiple}
                  disabled={disabled}
                />
              ))}
          </div>
        </div>

        {footer && <div className={styles.footer}>{footer}</div>}
      </div>

      {helperText && !errorText && (
        <Text as="span" className="mt-2" size="sm1">
          {helperText}
        </Text>
      )}
      {errorText && (
        <Text as="span" className="mt-2" color="red400" size="sm1">
          {errorText}
        </Text>
      )}
    </div>
  );
};

export default HierarchicalSelect;
